import { Button, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Text } from '@chakra-ui/react';
import { useKeystoneSession } from '../../auth/use-keystone-session';

import { lang } from '@lang';
import { useContext, type JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppContext, DesignActionTypes } from '@context';
import { deleteCustomerBlueprintRequest } from '@external';
import { getCanonicalDesignId } from '@helpers';
import { useAppToast } from '@hooks';
import { selectDeleteCustomerDraftsModal, selectNavigation, setContextPanelIsOpen, setDeleteCustomerDraftsModal, useAppDispatch, useAppSelector } from '@state';

export const DeleteCustomerDraftsModal = (): JSX.Element => {
	const appToast = useAppToast();

	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const state = useAppSelector(selectDeleteCustomerDraftsModal);
	const appNav = useAppSelector(selectNavigation);
	const { getToken } = useKeystoneSession();

	const { appDispatch, appState: { customerDesigns: designs }} = useContext(AppContext);

	const onClose = () => {
		dispatch(setDeleteCustomerDraftsModal(false));
	};

	const handleDelete = async (): Promise<void> => {
		const purges: Array<Promise<unknown>> = [];

		designs.designs.forEach((design) => {
			localStorage.removeItem(`autosave-${design.blueprint.id}`);
			appDispatch({ type: DesignActionTypes.setIsDirty, payload: { id: design.blueprint.id, isDirty: false }});

			// a draft-only design (no saved version) has nothing else to lose — purge it server-side too, so it can't resurrect on reload
			const hasSavedVersion = design.blueprint.kitchens.children.some((k) => k.version !== null);
			if (!hasSavedVersion && designs.email) {
				purges.push(deleteCustomerBlueprintRequest({ email: designs.email, submissionId: getCanonicalDesignId(design.blueprint), getToken }));
			}
		});

		appToast.success({ title: lang.customer.message.customerDraftsDeleted });

		dispatch(setDeleteCustomerDraftsModal(false));
		dispatch(setContextPanelIsOpen(false));

		// the reset-reload re-reads metadata.json, so the server purges must land first or the deleted drafts re-hydrate
		await Promise.all(purges);

		navigate(`/r/${appNav.email}/${appNav.designId}/?reset`);
	};

	return (
		<Modal isOpen={state.isOpen} onClose={onClose}>
			<ModalOverlay />
			<ModalContent>
				<ModalCloseButton />
				<>
					<ModalHeader>Warning</ModalHeader>
					<ModalBody>
						<Text>{lang.customer.message.deleteCustomerDraftsWarning}</Text>
					</ModalBody>

					<ModalFooter>
						<Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
						<Button colorScheme="green" onClick={handleDelete}>Continue</Button>
					</ModalFooter>
				</>
			</ModalContent>
		</Modal>
	);
};
